import { useDraggable, useDroppable } from '@dnd-kit/core';
import React, { useEffect, useRef, useState } from 'react';

import i18n from '../../../i18n';
import AppButton from '../buttons/AppButton';

import styles from './FileManager.module.scss';
import { ICONS, faClass } from '@/constants/icons';

const EXT_ICON = {
  pdf: 'fa-file-pdf',
  doc: 'fa-file-word',
  docx: 'fa-file-word',
  xls: 'fa-file-excel',
  xlsx: 'fa-file-excel',
  csv: 'fa-file-csv',
  png: 'fa-file-image',
  jpg: 'fa-file-image',
  jpeg: 'fa-file-image',
  gif: 'fa-file-image',
  webp: 'fa-file-image',
  zip: 'fa-file-zipper',
  rar: 'fa-file-zipper',
};

export const fileIcon = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  return EXT_ICON[ext] || 'fa-file';
};

export const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const isImage = (item) => (item.contentType || '').startsWith('image/');

/**
 * Voce del file manager (cartella o file), in vista griglia o lista.
 * Draggable via @dnd-kit (handle = corpo della voce); le cartelle sono anche
 * droppable (target di spostamento). Le azioni restano cliccabili.
 */
export default function FileEntry({ item, viewMode, readOnly, onOpen, onRename, onDelete }) {
  const isFolder = item.kind === 'folder';
  const [renaming, setRenaming] = useState(false);
  const [value, setValue] = useState(item.name);
  const inputRef = useRef(null);

  const {
    setNodeRef: setDragRef,
    listeners,
    attributes,
    isDragging,
  } = useDraggable({
    id: item.fullPath,
    data: { item },
    disabled: readOnly || renaming,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop:${item.fullPath}`,
    data: { targetPrefix: item.fullPath },
    disabled: !isFolder || readOnly,
  });

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  // `onBlur` scatta anche quando si annulla con Escape (l'input viene smontato):
  // senza questo flag l'annullamento rinominerebbe comunque.
  const cancelledRef = useRef(false);

  const startRename = () => {
    cancelledRef.current = false;
    setValue(item.name);
    setRenaming(true);
  };
  const cancelRename = () => {
    cancelledRef.current = true;
    setRenaming(false);
  };
  const submitRename = () => {
    setRenaming(false);
    if (cancelledRef.current) return;
    if (value.trim() && value.trim() !== item.name) onRename(item, value.trim());
  };

  const icon = isFolder ? 'fa-folder' : fileIcon(item.name);
  const iconClass = isFolder ? styles.folderIcon : styles.fileIcon;

  const actions = !readOnly && (
    <div className={styles.entryActions} onPointerDown={(e) => e.stopPropagation()}>
      <AppButton
        mode="icon"
        variant="neutral"
        size="sm"
        icon={ICONS.edit}
        title={i18n.t('rinomina', { defaultValue: 'Rinomina' })}
        onClick={startRename}
      />
      <AppButton
        mode="icon"
        variant="danger"
        size="sm"
        icon={ICONS.delete}
        title={i18n.t('elimina')}
        onClick={() => onDelete(item)}
      />
    </div>
  );

  const nameNode = renaming ? (
    <input
      ref={inputRef}
      className={styles.renameInput}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onBlur={submitRename}
      onKeyDown={(e) => {
        if (e.key === 'Enter') submitRename();
        if (e.key === 'Escape') cancelRename();
      }}
    />
  ) : (
    <span className={styles.name}>{item.name}</span>
  );

  const dropCls = isFolder && isOver ? styles.dropActive : '';
  const dragCls = isDragging ? styles.dragging : '';

  // Vista griglia: tile.
  if (viewMode === 'cards') {
    return (
      <div ref={setDropRef} className={`${styles.tile} ${dropCls} ${dragCls}`}>
        <div
          ref={setDragRef}
          className={styles.tileBody}
          role="button"
          tabIndex={0}
          onClick={() => !renaming && onOpen(item)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !renaming && onOpen(item)}
          {...listeners}
          {...attributes}
        >
          <div className={styles.thumb}>
            {!isFolder && isImage(item) ? (
              <img src={item.url} alt="" loading="lazy" />
            ) : (
              <i className={faClass(icon, iconClass)} />
            )}
          </div>
          {nameNode}
          {!isFolder && item.size != null && (
            <span className={styles.meta}>{formatBytes(item.size)}</span>
          )}
        </div>
        {actions}
      </div>
    );
  }

  // Vista lista: riga.
  return (
    <div ref={setDropRef} className={`${styles.row} ${dropCls} ${dragCls}`}>
      <div
        ref={setDragRef}
        className={styles.rowMain}
        role="button"
        tabIndex={0}
        onClick={() => !renaming && onOpen(item)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !renaming && onOpen(item)}
        {...listeners}
        {...attributes}
      >
        <i className={faClass(icon, `${iconClass} ${styles.rowIcon}`)} />
        {nameNode}
      </div>
      <span className={styles.colSize}>{isFolder ? '—' : formatBytes(item.size)}</span>
      <span className={styles.colDate}>
        {item.updated ? new Date(item.updated).toLocaleDateString('it-IT') : '—'}
      </span>
      {actions}
    </div>
  );
}
